<script lang="ts" setup>
import { computed, inject, ref, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import type BigNumber from '@exchange/helpers/bignumber';

interface OrderBalanceBySide {
  code: string;
  precision: number;
  available: BigNumber;
  locked: BigNumber;
  marginAvailable?: BigNumber;
}

interface FormBalance {
  id: string;
  label: string;
  value: BigNumber;
}

const props = defineProps({
  balance: { type: Object as PropType<OrderBalanceBySide>, default: () => {} },
  showLoading: { type: Boolean, required: true },
  isFutures: { type: Boolean, required: true },
  maxPositionSize: { type: Object as PropType<BigNumber>, required: true },
});

const { t } = useI18n({ useScope: 'global' });
const balanceMasked = inject('balanceMasked', ref(false));

const balances = computed<Array<FormBalance>>(() => [
  {
    id: 'available',
    label: t('modules.orderForm.availableBalance'),
    value: props.balance.available,
  },
  {
    id: 'locked',
    label: t('modules.orderForm.lockedBalance'),
    value: props.balance.locked,
  },
]);

const futuresBalance = computed(() => [
  {
    id: 'maxPositionSize',
    label: t('modules.orderForm.maxPositionSize'),
    value: props.maxPositionSize,
  },
]);

const code = computed(() => props.balance.code);
const precision = computed(() => props.balance.precision);
</script>

<template>
  <div class="order-form-balance">
    <div
      v-for="item in isFutures ? futuresBalance : balances"
      :key="item.id"
      class="order-form-balance__item"
    >
      <div class="order-form-balance__label">{{ item.label }}</div>
      <div class="order-form-balance__data">
        <ot-balance-loading
          :loading-until="showLoading"
          :value="item.value"
          :precision="precision"
          :symbol="code"
          :light-symbol="false"
          :masked="balanceMasked"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.order-form-balance {
  color: rgb(var(--v-theme-text-secondary));
  font-size: var(--font-size-sm);

  &__item {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 4px;
    gap: 4px;
  }

  &__data {
    color: rgb(var(--v-theme-text-primary));
  }
}
</style>
